/*
 * @Description: 隐私政策渲染
 * @params: 页面参数：?type=index&country=TH&lang=en-US
 * @Author: 刘宝顺
 * @Date: 2024-03-01 17:36:10
 * @LastEditTime: 2024-07-29 09:50:40
 * @LastEditors: 刘宝顺
 */
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

// import enhtml from '@/assets/html/en.html';

import { COUNTRY_LANG_EN_US, COUNTRY_LANG_TH_TH, COUNTRY_LANG_ZH_CN } from '@/constants/country';
import '@/styles/common.scss';
import { transformAppType } from '@/utils/common';
import { changeLanguage, useI18n } from '@/utils/i18next';
import { getURLParameters } from '@/utils/tool';

import type { TCountryIsoCode, TCountryLangCode } from '@/constants/country';

type TType =
    | 'californiaConsumerPrivacyStatement' // 给加利福尼亚的通知
    | 'childrenPrivacy' // 儿童隐私政策
    | 'index' // 主隐私政策
    | 'noticeToColoradoConnecticutUtahVirginiaResidents' // 给科罗拉多州、康涅狄格州、犹他州、弗吉尼亚州的通知
    | 'noticeToNevadaResidents' // 给内华达州的通知
    | 'sms' // 短信隐私政策
    | 'thirdPartners' // 三方合作方
    | 'thirdPartyTracking'; // 三方技术跟踪
interface IPageParams {
    appType: 'JETX' | 'YIGOLI' | undefined;
    type: TType; // 隐私政策类型
    country: TCountryIsoCode;
    lang: TCountryLangCode;
}
// eslint-disable-next-line max-lines-per-function
function PrivacyAgreement() {
    const { t } = useI18n();
    const [html, setHtml] = useState();
    // 下面配置的是每个国家不同语言不同类型的隐私政策，如果某个国家的语言没做国际化适配，在配置的时候也要把对应语言配置上，链接可以用已有的语言，这样做是为了防止APP跳转过来时访问了一个不支持的语言导致报错（目前只针对index项这样处理就行，其他的链接入口都是在index里跳转的，不需要做兼容配置，有啥就配置啥）
    const policyObj = {
        index: {
            CN: {
                [COUNTRY_LANG_ZH_CN]: '/public/static/word/privacyPolicy/CN/China_zhCN.html',
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/CN/China_zhCN.html',
                [COUNTRY_LANG_TH_TH]: '/public/static/word/privacyPolicy/CN/China_zhCN.html',
            },
            US: {
                [COUNTRY_LANG_ZH_CN]: '/public/static/word/privacyPolicy/US/America_enUS.html',
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/America_enUS.html',
                [COUNTRY_LANG_TH_TH]: '/public/static/word/privacyPolicy/US/America_enUS.html',
            },
            TH: {
                [COUNTRY_LANG_ZH_CN]: '/public/static/word/privacyPolicy/TH/Thailand_enUS.html',
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/TH/Thailand_enUS.html',
                [COUNTRY_LANG_TH_TH]: '/public/static/word/privacyPolicy/TH/Thailand_thTH.html',
            },
        },
        childrenPrivacy: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Children’s-Privacy-Policy-enUS.html',
            },
        },
        sms: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/SMS-Policy-enUS.html',
            },
        },
        thirdPartners: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Third-party-partners-enUS.html',
            },
            TH: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Third-party-partners-enUS.html', // 没针对泰国单独出英文版的，直接使用美国的，目前内容都一样
                [COUNTRY_LANG_TH_TH]: '/public/static/word/privacyPolicy/TH/Third-party-partners-thTH.html',
            },
        },
        thirdPartyTracking: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Third-party-technical-tracking-enUS.html',
            },
            TH: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/TH/Third-party-technical-tracking-enUS.html',
                [COUNTRY_LANG_TH_TH]: '/public/static/word/privacyPolicy/TH/Third-party-technical-tracking-thTH.html',
            },
        },
        californiaConsumerPrivacyStatement: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/CALIFORNIA-CONSUMER-PRIVACY-STATEMENT-enUS.html',
            },
        },
        noticeToColoradoConnecticutUtahVirginiaResidents: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Notice-to-Colorado-Connecticut-Utah-and-Virginia-Residents-enUS.html',
            },
        },
        noticeToNevadaResidents: {
            US: {
                [COUNTRY_LANG_EN_US]: '/public/static/word/privacyPolicy/US/Notice-to-Nevada-Residents-enUS.html',
            },
        },
    };

    useEffect(() => {
        // console.log('enhtml---', enhtml);
        const param: IPageParams | object = getURLParameters();

        if (param.appType === 'JETX') {
            document.title = 'JETX';
        }
        // console.log(param, param.type, param.country, param.lang, policyObj);

        const fetchData = async (url: string) => {
            const response = await fetch(url);
            let data = '';
            if (response.status === 200) {
                data = await response.text();
            }
            setHtml(data);
        };
        const path = policyObj[param.type]?.[param.country]?.[param.lang] || policyObj.index.US[COUNTRY_LANG_EN_US];
        fetchData(transformAppType(path));
    }, []);

    return (
        <div className="box">
            <div dangerouslySetInnerHTML={{ __html: html }} />
            {html === '' && <div>暂无内容</div>}
        </div>
    );
}

const container = document.getElementById('root');
const root = createRoot(container!); // createRoot(container!) if you use TypeScript
root.render(<PrivacyAgreement />);
